import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { Account } from './account';
export { Account };
import { accountSchema } from '@schemas/business-objects/account';
import { AccountUserFormArray } from './account-user-form-group';
import { SupportAttachmentFormArray } from './support-attachment-form-group';
import { SupportCaseFormArray } from './support-case-form-group';
import { SupportCommentFormArray } from './support-comment-form-group';
import type { TenantConfig } from '../interface-definitions/index';
import type { User } from './user';

export class AccountFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    name: SchemaFormControl;
    designerSeats: SchemaFormControl;
    renewalDate: SchemaFormControl;
    slackWebhookUrl: SchemaFormControl;
    accountUsers: AccountUserFormArray;
    cases: SupportCaseFormArray;
    comments: SupportCommentFormArray;
    attachments: SupportAttachmentFormArray;
  };

  declare value: Partial<Account>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(accountSchema, Account, data, options);
  }

  get object(): Account {
    return new Account(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'accountUsers':
        return new AccountUserFormArray();
      case 'cases':
        return new SupportCaseFormArray();
      case 'comments':
        return new SupportCommentFormArray();
      case 'attachments':
        return new SupportAttachmentFormArray();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<Account[]> {
    return Account.find(filter);
  }

  static async findOne(filter?: any): Promise<Account | null> {
    return Account.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<Account> {
    return Account.findById(id, filter);
  }

  static async create(data: Partial<Account>): Promise<Account> {
    return Account.create(data as any);
  }

  static async updateById(
    id: number,
    updates: Partial<Account>,
  ): Promise<Account> {
    return Account.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return Account.deleteById(id);
  }

  async addMember(email: string, admin?: boolean): Promise<any> {
    const instance = new Account(this.getRawValue());
    return instance.addMember(email, admin);
  }

  static async currentUser(filter: any): Promise<any[]> {
    return Account.currentUser(filter);
  }

  async members(): Promise<any[]> {
    const instance = new Account(this.getRawValue());
    return instance.members();
  }

  async addMembers(body: { emails: string }): Promise<any> {
    const instance = new Account(this.getRawValue());
    return instance.addMembers(body);
  }

  static async findOneWithProfile(
    filter: any,
  ): Promise<AccountFormGroup | null> {
    const result = await Account.findOneWithProfile(filter);
    if (!result) return null;
    const fg = new AccountFormGroup();
    fg._populate(result);
    return fg;
  }

  async setSlackWebhookUrl(webhookUrl: string | null): Promise<any> {
    const instance = new Account(this.getRawValue());
    return instance.setSlackWebhookUrl(webhookUrl);
  }
}

export class AccountFormArray extends PersistedFormArray {
  declare controls: AccountFormGroup[];

  readonly entityName = 'Account' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(accountSchema, Account, options);
  }

  protected override createItemGroup() {
    return new AccountFormGroup();
  }

  get array(): Account[] {
    return this.controls.map((group: AccountFormGroup) => group.object);
  }
}

export class AccountPersistedArray extends PersistedArray<Account> {
  readonly entityName = 'Account' as const;

  constructor(options?: PersistedArrayOptions) {
    super(Account, options);
  }
}
